import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle,
  Calendar,
  MapPin,
  Users,
  Phone,
  Mail,
  ArrowRight,
  Clock,
  Luggage,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { formatFranceDateTime } from "@/lib/time-utils";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Google Ads Conversion ID and Label
const GOOGLE_ADS_CONVERSION_ID =
  process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID || "AW-17719340205";
// You can customize this label

const ThankYou = () => {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("bookingId");
  const isQuoteRequest = searchParams.get("quote") === "true";
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const conversionFired = useRef(false);

  useEffect(() => {
    if (bookingId) {
      fetchBooking();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  useEffect(() => {
    if (booking && !conversionFired.current && !isQuoteRequest) {
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-17719340205/O_6aCPKVm9IbEK3ZnoFC",
          value: booking.total_price,
          currency: "EUR",
          transaction_id: booking.booking_id || bookingId,
          user_data: {
            email: booking.email?.trim().toLowerCase(),
            phone_number: booking.phone?.replace(/\D/g, ""),
          },
        });
      }

      if (window.dataLayer) {
        window.dataLayer.push({
          event: "generate_lead",
          value: booking.total_price,
          currency: "EUR",
          transaction_id: booking.booking_id || bookingId,
        });
      }

      // GA4 purchase event
      if (window.dataLayer) {
        window.dataLayer.push({
          event: "purchase",
          ecommerce: {
            transaction_id: booking.booking_id || bookingId,
            value: booking.total_price,
            currency: "EUR",
            items: [
              {
                item_name: `${booking.pickup_location} to ${booking.dropoff_location}`,
                item_category: booking.trip_type || "transfer",
                price: booking.total_price,
                quantity: 1,
              },
            ],
          },
        });
        console.log("GA4 purchase event fired");
      }

      conversionFired.current = true;
    }
  }, [booking, isQuoteRequest, bookingId]);

  const fetchBooking = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}`);
      if (response.ok) {
        const data = await response.json();
        setBooking(data);
      }
    } catch (error) {
      console.error("Error fetching booking:", error);
    } finally {
      setLoading(false);
    }
  };

  // Determine if this is a quote request (from URL param or booking data)
  const requiresQuote = isQuoteRequest || booking?.requires_quote;

  return (
    <div
      className="min-h-screen pt-20 pb-16 bg-slate-50"
      data-testid="thank-you-page"
    >
      <div className="container mx-auto px-4 max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-soft p-8 md:p-12 text-center"
        >
          {/* Success Icon - Different color for quotes */}
          <div
            className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
              requiresQuote ? "bg-amber-100" : "bg-green-100"
            }`}
          >
            {requiresQuote ? (
              <Clock className="w-10 h-10 text-amber-600" />
            ) : (
              <CheckCircle className="w-10 h-10 text-green-600" />
            )}
          </div>

          <h1 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mb-4">
            {requiresQuote ? "Quote Request Received!" : "Booking Confirmed!"}
          </h1>

          <p className="text-lg text-slate-600 mb-8">
            {requiresQuote
              ? "Thank you! We've received your request and will contact you with a personalized quote within 2 hours."
              : "Thank you for booking with Best Disney Transfer. Your driver will contact you before pickup."}
          </p>

          {/* Booking/Reference ID */}
          {bookingId && (
            <div
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-full mb-8 ${
                requiresQuote
                  ? "bg-amber-50 text-amber-700"
                  : "bg-primary-50 text-primary-700"
              }`}
            >
              <span className="text-sm font-medium">Reference ID:</span>
              <span className="font-bold">{bookingId}</span>
            </div>
          )}

          {/* Booking Details */}
          {booking && (
            <div className="text-left bg-slate-50 rounded-xl p-6 mb-8">
              <h2 className="font-heading font-bold text-lg text-slate-900 mb-4">
                {requiresQuote ? "Request Details" : "Booking Details"}
              </h2>

              <div className="space-y-3 text-sm">
                {/* <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-slate-500">Route</p>
                    <p className="font-medium text-slate-900">
                      {booking.pickup_location} → {booking.dropoff_location}
                    </p>
                  </div>
                </div> */}

                {booking.trip_type === "return" ? (
                  <div className="space-y-3">
                    {/* Outbound */}
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-slate-500">Route</p>
                        <p className="font-medium text-slate-900">
                          {booking.pickup_location} → {booking.dropoff_location}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {formatFranceDateTime(booking.pickup_datetime)}
                        </p>
                      </div>
                    </div>

                    {/* Return */}
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-slate-500">Return</p>
                        <p className="font-medium text-slate-900">
                          {booking.return_pickup_location} →{" "}
                          {booking.return_dropoff_location}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {formatFranceDateTime(booking.return_pickup_datetime)}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* ONE-WAY FALLBACK */
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-slate-500">Route</p>
                      <p className="font-medium text-slate-900">
                        {booking.pickup_location} → {booking.dropoff_location}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-slate-500">Date & Time</p>
                    <p className="font-medium text-slate-900">
                      {formatFranceDateTime(booking.pickup_datetime, {
                        showYear: true,
                      })}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-slate-500">Passengers</p>
                    <p className="font-medium text-slate-900">
                      {`${booking.adults} ${booking.adults === 1 ? "Adult" : "Adults"}, ${booking.kids} ${booking.kids === 1 ? "Kid" : "Kids"}`}{" "}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Luggage className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-slate-500">Luggages</p>
                    <p className="font-medium text-slate-900">
                      {booking.hand_luggages} hand + {booking.checked_luggages}{" "}
                      checked
                    </p>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-3 mt-3">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Payment Method</span>
                    <span className="font-medium text-slate-900 capitalize">
                      Pay Driver by {booking.payment_method}
                    </span>
                  </div>
                  <div className="flex justify-between mt-2">
                    <span className="text-slate-500">
                      {requiresQuote ? "Price" : "Total Price"}
                    </span>
                    {requiresQuote ? (
                      <span className="font-heading font-bold text-lg text-amber-600">
                        Pending Quote
                      </span>
                    ) : (
                      <span className="font-heading font-bold text-xl text-primary-600">
                        €{booking.total_price}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Status Badge */}
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 ${
              requiresQuote
                ? "bg-amber-50 text-amber-700"
                : "bg-green-50 text-green-700"
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${requiresQuote ? "bg-amber-500" : "bg-green-500"}`}
            ></span>
            <span className="font-medium">
              {requiresQuote ? "PENDING QUOTE" : "CONFIRMED (PAY ON ARRIVAL)"}
            </span>
          </div>

          {/* What's Next - Different for quotes */}
          {requiresQuote ? (
            <div className="text-left bg-amber-50 rounded-xl p-6 mb-8">
              <h3 className="font-heading font-bold text-slate-900 mb-3">
                What's Next?
              </h3>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  We'll review your custom location request
                </li>
                <li className="flex items-start gap-2">
                  <Mail className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  You'll receive a personalized quote via email within 2 hours
                </li>
                <li className="flex items-start gap-2">
                  <Phone className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  If urgent, call us and quote your reference number
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  Once you approve the quote, your booking will be confirmed
                </li>
              </ul>
            </div>
          ) : (
            <div className="text-left bg-primary-50 rounded-xl p-6 mb-8">
              <h3 className="font-heading font-bold text-slate-900 mb-3">
                What's Next?
              </h3>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  Confirmation email sent to your inbox
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  Driver will contact you 24 hours before pickup
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  Look for driver with your name sign at pickup
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  Pay driver directly on arrival
                </li>
              </ul>
            </div>
          )}

          {/* Contact Info */}
          <div className="flex flex-wrap justify-center gap-4 mb-8 text-sm">
            <a
              href="tel:+33123456789"
              className="flex items-center gap-2 text-midnight-navy hover:bg-champagne-gold transition-colors"
            >
              <Phone className="w-4 h-4" />
              +33 763 519 524
            </a>
            <a
              href="mailto:support@bestdisneytransfer.com"
              className="flex items-center gap-2 text-midnight-navy hover:bg-champagne-gold/500  transition-colors"
            >
              <Mail className="w-4 h-4" />
              info@bestdisneytransfer.com
            </a>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              asChild
              className="bg-primary-600 hover:bg-primary-700 rounded-full px-8"
            >
              <Link to="/">
                Back to Home
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full px-8">
              <Link to="/booking">Book Another Transfer</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ThankYou;
